import { debugMessage } from "../common";
import { getChartMissingDataFraction } from "./groupUnitData";
import { getStaffForceSource } from "./staffForceSource";

const DB_NAME = "p3portal-chart-cache";
const DB_VERSION = 1;
const STORE_NAME = "entries";
const CACHE_KEY_VERSION = "v2";

/** Matches Charts/Totals background refresh interval */
const FRESH_MS = 5 * 60 * 1000;

/** Do not cache (and evict) when more than this fraction of interval values are null */
export const CHART_CACHE_MAX_MISSING_FRACTION = 0.01;

let dbPromise = null;
let openDbInstance = null;
let dbGeneration = 0;

/** Set when telemetry cache is cleared from Help; consumed when Help closes. */
let telemetryCacheClearedPendingRefetch = false;

const CHART_CACHE_CLEARED_EVENT = "p3portal-chart-cache-cleared";

/** In-flight chart unit loads keyed by cache key (shared by fetch + prefetch). */
const inFlightChartUnitRequests = new Map();

/** Session-only staff toggle from Dev Tools (not persisted). */
let chartCacheDisabledByStaff = false;

function _debug(message) {
  debugMessage(message, "chartDataCache");
}

export function isChartCacheDisabledByStaff() {
  return chartCacheDisabledByStaff;
}

/** When disabled, clears IndexedDB chart cache and skips reads/writes until re-enabled. */
export async function setChartCacheDisabledByStaff(disabled) {
  chartCacheDisabledByStaff = disabled;
  try {
    if (disabled) {
      await clearChartDataCache();
      _debug("staff disabled chart/totals cache");
    } else {
      _debug("staff re-enabled chart/totals cache");
    }
  } catch (err) {
    _debug(`setChartCacheDisabledByStaff failed: ${err.message}`);
  }
}

/** Same IndexedDB wipe as Help → Clear Telemetry Cache. */
export async function clearTelemetryCache() {
  await clearChartDataCache();
}

export function markTelemetryCacheClearedForRefetch() {
  telemetryCacheClearedPendingRefetch = true;
}

/** Dispatch refetch event once if telemetry cache was cleared this Help session. */
export function dispatchTelemetryCacheRefetchIfPending() {
  if (!telemetryCacheClearedPendingRefetch) return false;
  telemetryCacheClearedPendingRefetch = false;
  window.dispatchEvent(new CustomEvent(CHART_CACHE_CLEARED_EVENT));
  _debug("dispatched chart cache refetch");
  return true;
}

export function subscribeChartCacheCleared(listener) {
  window.addEventListener(CHART_CACHE_CLEARED_EVENT, listener);
  return () => window.removeEventListener(CHART_CACHE_CLEARED_EVENT, listener);
}

function closeTrackedDbConnection() {
  const db = openDbInstance;
  openDbInstance = null;
  dbPromise = null;

  if (!db) return;

  try {
    db.close();
  } catch (err) {
    _debug(`closeTrackedDbConnection failed: ${err.message}`);
  }
}

function deleteChartCacheDatabase() {
  return new Promise((resolve) => {
    let settled = false;

    const finish = (message) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      _debug(message);
      resolve();
    };

    const timeoutId = setTimeout(
      () => finish("deleteDatabase timed out"),
      5000,
    );

    try {
      const request = indexedDB.deleteDatabase(DB_NAME);
      request.onsuccess = () => finish("deleted chart cache database");
      request.onerror = () =>
        finish(`deleteDatabase error: ${request.error?.message ?? "unknown"}`);
      request.onblocked = () => {
        _debug("deleteDatabase blocked — closing tracked connection");
        closeTrackedDbConnection();
      };
    } catch (err) {
      finish(`deleteDatabase threw: ${err.message}`);
    }
  });
}

export function getOrCreateInFlightChartRequest(cacheKey, factory) {
  const existing = inFlightChartUnitRequests.get(cacheKey);
  if (existing) {
    _debug(`in-flight join ${cacheKey}`);
    return existing;
  }

  const promise = factory();
  inFlightChartUnitRequests.set(cacheKey, promise);
  promise.finally(() => {
    if (inFlightChartUnitRequests.get(cacheKey) === promise) {
      inFlightChartUnitRequests.delete(cacheKey);
    }
  });
  return promise;
}

function openDb() {
  if (!window.indexedDB) {
    return Promise.reject(new Error("IndexedDB not available"));
  }

  if (!dbPromise) {
    const openGeneration = dbGeneration;
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onerror = () => {
        if (openGeneration !== dbGeneration) {
          dbPromise = null;
          reject(new Error("IndexedDB open aborted"));
          return;
        }
        dbPromise = null;
        openDbInstance = null;
        reject(request.error);
      };
      request.onsuccess = () => {
        const db = request.result;
        if (openGeneration !== dbGeneration) {
          dbPromise = null;
          try {
            db.close();
          } catch (_) {}
          reject(new Error("IndexedDB open aborted"));
          return;
        }
        openDbInstance = db;
        db.onversionchange = () => {
          try {
            db.close();
          } catch (_) {}
        };
        resolve(db);
      };
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: "key" });
          store.createIndex("hardwareId", "hardwareId", { unique: false });
          store.createIndex("cachedAt", "cachedAt", { unique: false });
          store.createIndex("mode", "mode", { unique: false });
        }
      };
    });
  }

  return dbPromise;
}

/**
 * Cache key includes hardwareId so each unit has isolated entries when switching units.
 * Format: v2:{mode}:{hardwareId}:{timeFromMs}:{timeToMs}:{timePreset}[:forceSource]
 */
export function buildChartCacheKey({
  mode,
  hardwareId,
  timeFromMs,
  timeToMs,
  timePreset = "",
}) {
  const forceSource = getStaffForceSource();
  const forceSuffix = forceSource ? `:${forceSource}` : "";
  return `${CACHE_KEY_VERSION}:${mode}:${hardwareId}:${timeFromMs}:${timeToMs}:${timePreset}${forceSuffix}`;
}

export function getChartCacheMode({ totalsOnly, liveView }) {
  if (liveView) return "liveView";
  if (totalsOnly) return "totals";
  return "chart";
}

/**
 * Historical ranges are kept indefinitely; "today" ranges refresh after FRESH_MS.
 *
 * Uses a 2-hour grace window instead of UTC midnight to decide when a day
 * becomes "historical". This avoids a BST/UTC mismatch where chart days
 * running on London midnight (23:00 UTC during BST) could be permanently
 * locked into the cache with stale data for the last ~35-40 minutes of the
 * London day if the boundary used UTC midnight.
 */
export function isChartCacheEntryStale(entry, endUtcMs) {
  if (!entry?.cachedAt) return true;

  const rangeEnd = Number(endUtcMs);
  if (!Number.isFinite(rangeEnd)) return true;

  const HISTORICAL_GRACE_MS = 2 * 60 * 60 * 1000; // 2 hours
  if (rangeEnd < Date.now() - HISTORICAL_GRACE_MS) {
    return false;
  }

  const ageMs = Date.now() - new Date(entry.cachedAt).getTime();
  return ageMs > FRESH_MS;
}

function telemetryToChartUnitsData(telemetry, unitContext) {
  if (!Array.isArray(telemetry) || !telemetry.length) return null;

  const unit =
    unitContext ??
    (unitContext?.hardwareId != null ? { hardwareId: unitContext.hardwareId } : null);

  return { unit, telemetry };
}

/** Share of 5-min intervals with chart missingData (same pipeline as Chart.js), 0 if unknown. */
export function getChartDataMissingFraction(telemetry, unitContext = null) {
  const unitsData = telemetryToChartUnitsData(telemetry, unitContext);
  if (!unitsData) return 0;
  return getChartMissingDataFraction(unitsData);
}

export function isChartCacheDataTooSparse(telemetry, unitContext = null) {
  if (!Array.isArray(telemetry) || !telemetry.length) return false;
  return (
    getChartDataMissingFraction(telemetry, unitContext) >
    CHART_CACHE_MAX_MISSING_FRACTION
  );
}

function clearInFlightChartRequestsForHardwareId(hardwareId) {
  const id = String(hardwareId);
  for (const key of inFlightChartUnitRequests.keys()) {
    if (key.split(":")[2] === id) {
      inFlightChartUnitRequests.delete(key);
    }
  }
}

/** Remove all cached chart/totals entries for one unit (e.g. when staff changes forceSource). */
export async function clearChartCacheForHardwareId(hardwareId) {
  if (hardwareId == null || hardwareId === "") return;

  clearInFlightChartRequestsForHardwareId(hardwareId);

  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index("hardwareId");
      const request = index.openCursor(IDBKeyRange.only(String(hardwareId)));

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          _debug(`cleared chart cache for hardwareId ${hardwareId}`);
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });
  } catch (err) {
    _debug(`clearChartCacheForHardwareId failed: ${err.message}`);
  }
}

/**
 * Staff Dev Tools "Refresh current day": evict the cached chart + totals
 * entries for the given day/units, then fire the chart-cache-cleared event so
 * the visible Charts/Totals reload from the network on the next fetch.
 *
 * Time range mirrors how Charts/Totals build their day request: the day start
 * moment as-is, and end-of-day in the unit timezone.
 */
export async function refreshChartCacheForDay({ units, dayLocal, unitTz }) {
  if (dayLocal && Array.isArray(units) && units.length) {
    const timeFromMs = dayLocal.clone().valueOf();
    const timeToMs = dayLocal.clone().tz(unitTz).endOf("day").valueOf();

    for (const unit of units) {
      const hardwareId = unit?.hardwareId;
      if (hardwareId == null || hardwareId === "") continue;

      clearInFlightChartRequestsForHardwareId(hardwareId);

      for (const mode of ["chart", "totals"]) {
        const key = buildChartCacheKey({
          mode,
          hardwareId,
          timeFromMs,
          timeToMs,
          timePreset: "",
        });
        await deleteChartCacheEntry(key);
      }
    }
  }

  window.dispatchEvent(new CustomEvent(CHART_CACHE_CLEARED_EVENT));
  _debug("staff refreshed current day");
}

export async function deleteChartCacheEntry(key) {
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(key);
      request.onsuccess = () => {
        _debug(`deleted cache entry ${key}`);
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  } catch (err) {
    _debug(`deleteChartCacheEntry failed: ${err.message}`);
  }
}

export async function getChartCacheEntry(key) {
  if (isChartCacheDisabledByStaff()) return null;

  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  } catch (err) {
    _debug(`getChartCacheEntry failed: ${err.message}`);
    return null;
  }
}

export async function setChartCacheEntry({
  key,
  mode,
  hardwareId,
  timeFromMs,
  timeToMs,
  timePreset,
  telemetry,
  totals,
  requestedTimeFromMs,
  requestedTimeToMs,
}) {
  if (isChartCacheDisabledByStaff()) return;

  if (
    mode !== "totals" &&
    (!Array.isArray(telemetry) ||
      !telemetry.length ||
      isChartCacheDataTooSparse(telemetry, { hardwareId }))
  ) {
    await deleteChartCacheEntry(key);
    _debug(
      `skip cache (>${(CHART_CACHE_MAX_MISSING_FRACTION * 100).toFixed(0)}% missing, ${(getChartDataMissingFraction(telemetry, { hardwareId }) * 100).toFixed(1)}%) ${key}`,
    );
    return;
  }

  try {
    const db = await openDb();
    const entry = {
      key,
      mode,
      hardwareId,
      timeFromMs,
      timeToMs,
      timePreset,
      telemetry,
      totals,
      requestedTimeFromMs,
      requestedTimeToMs,
      cachedAt: new Date().toISOString(),
    };

    await new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(entry);
      request.onsuccess = () => {
        _debug(`cached ${key}`);
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  } catch (err) {
    _debug(`setChartCacheEntry failed: ${err.message}`);
  }
}

export async function clearChartDataCache() {
  inFlightChartUnitRequests.clear();

  if (!window.indexedDB) return;

  dbGeneration += 1;
  closeTrackedDbConnection();
  await deleteChartCacheDatabase();
}

export async function getChartCacheEntryCount() {
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } catch {
    return 0;
  }
}
